import {
  AlertTriangle,
  CalendarClock,
  Clock,
  IdCard,
  Images,
  KeyRound,
  Mail,
  MapPin,
  QrCode,
  RotateCw,
  ShieldCheck,
  Smartphone,
} from 'lucide-react';
import {COUNTRY_CODES} from '@chekin/core';
import {VERIFICATION_METHODS} from '@chekin/api';
import {VelaScene, type VelaContext, type VelaRule} from '../engine/types';
import {photoTipRules} from './photoTips.rules';

const PRIVACY_SCENES = [
  VelaScene.IVStart,
  VelaScene.IVDocumentSelection,
  VelaScene.IVFront,
  VelaScene.IVBack,
  VelaScene.IVSelfie,
];

const isEmailChannel = (ctx: VelaContext) =>
  ctx.contacts.channel === VERIFICATION_METHODS.email;

export const ivRules: VelaRule[] = [
  {
    id: 'iv/start-what-you-need',
    section: 'goodToKnow',
    scenes: [VelaScene.IVStart, VelaScene.IVDocumentSelection],
    priority: 20,
    evaluate: (_ctx, t) => ({
      id: 'start-what-you-need',
      icon: <IdCard />,
      title: t('card.have_id_ready.title'),
      description: t('card.have_id_ready.body'),
    }),
  },
  {
    id: 'iv/privacy-protected',
    section: 'goodToKnow',
    scenes: PRIVACY_SCENES,
    priority: 30,
    evaluate: (_ctx, t) => ({
      id: 'privacy-protected',
      title: t('card.privacy_protected.title'),
      description: t('card.privacy_protected.body'),
    }),
  },
  {
    id: 'iv/unavailable-opens-later',
    section: 'whyYouCantStart',
    scenes: [VelaScene.IVUnavailable],
    priority: 20,
    evaluate: (_ctx, t) => ({
      id: 'unavailable-opens-later',
      icon: <CalendarClock />,
      title: t('card.unavailable_opens_later.title'),
      description: t('card.unavailable_opens_later.body'),
    }),
  },
  {
    id: 'iv/unavailable-worth-it',
    section: 'whyYouCantStart',
    scenes: [VelaScene.IVUnavailable],
    priority: 10,
    evaluate: (_ctx, t) => ({
      id: 'unavailable-worth-it',
      icon: <KeyRound />,
      title: t('card.unavailable_worth_it.title'),
      description: t('card.unavailable_worth_it.body'),
    }),
  },
  {
    id: 'iv/qr-look-at-the-entrance',
    section: 'findingTheCode',
    scenes: [VelaScene.IVQRScan],
    priority: 20,
    evaluate: (_ctx, t) => ({
      id: 'qr-look-at-the-entrance',
      icon: <MapPin />,
      title: t('card.qr_look_at_the_entrance.title'),
      description: t('card.qr_look_at_the_entrance.body'),
    }),
  },
  {
    id: 'iv/qr-use-the-printed-sticker',
    section: 'findingTheCode',
    scenes: [VelaScene.IVQRScan],
    priority: 10,
    evaluate: (_ctx, t) => ({
      id: 'qr-use-the-printed-sticker',
      icon: <QrCode />,
      title: t('card.qr_use_the_printed_sticker.title'),
      description: t('card.qr_use_the_printed_sticker.body'),
    }),
  },
  ...photoTipRules,
  {
    id: 'iv/why-photos',
    section: 'goodToKnow',
    scenes: [VelaScene.IVStart, VelaScene.IVDocumentSelection],
    evaluate: (ctx, t) =>
      ctx.countryCode === COUNTRY_CODES.spain
        ? {
            id: 'why-photos',
            icon: <Images />,
            title: t('card.why_photos.title'),
            description: t('card.why_photos.body'),
          }
        : null,
  },
  {
    id: 'iv/contacts-why',
    section: 'whyVerify',
    scenes: [VelaScene.IVContacts],
    priority: 20,
    evaluate: (ctx, t) =>
      ctx.contacts.step === 'input'
        ? isEmailChannel(ctx)
          ? {
              id: 'contacts-why-email',
              icon: <Mail />,
              title: t('card.contacts_why_email.title'),
              description: t('card.contacts_why_email.body'),
            }
          : {
              id: 'contacts-why-phone',
              icon: <Smartphone />,
              title: t('card.contacts_why_phone.title'),
              description: t('card.contacts_why_phone.body'),
            }
        : null,
  },
  {
    id: 'iv/contacts-never-shared',
    section: 'whyVerify',
    scenes: [VelaScene.IVContacts],
    priority: 10,
    evaluate: (ctx, t) =>
      ctx.contacts.step === 'input' && isEmailChannel(ctx)
        ? {
            id: 'contacts-never-shared',
            icon: <ShieldCheck />,
            title: t('card.contacts_never_shared.title'),
            description: t('card.contacts_never_shared.body'),
          }
        : null,
  },
  {
    id: 'iv/code-not-arrived',
    section: 'aboutYourCode',
    scenes: [VelaScene.IVContacts],
    priority: 20,
    evaluate: (ctx, t) =>
      ctx.contacts.step === 'code' && !ctx.contacts.hasError
        ? isEmailChannel(ctx)
          ? {
              id: 'code-not-arrived-email',
              icon: <Mail />,
              title: t('card.code_not_arrived_email.title'),
              description: t('card.code_not_arrived_email.body'),
            }
          : {
              id: 'code-not-arrived-sms',
              icon: <Smartphone />,
              title: t('card.code_not_arrived_sms.title'),
              description: t('card.code_not_arrived_sms.body'),
            }
        : null,
  },
  {
    id: 'iv/codes-expire',
    section: 'aboutYourCode',
    scenes: [VelaScene.IVContacts],
    priority: 10,
    evaluate: (ctx, t) =>
      ctx.contacts.step === 'code' && !ctx.contacts.hasError && isEmailChannel(ctx)
        ? {
            id: 'codes-expire',
            icon: <Clock />,
            title: t('card.codes_expire.title'),
            description: t('card.codes_expire.body'),
          }
        : null,
  },
  {
    id: 'iv/wrong-code-typos',
    section: 'wrongCode',
    scenes: [VelaScene.IVContacts],
    priority: 20,
    evaluate: (ctx, t) =>
      ctx.contacts.hasError
        ? {
            id: 'wrong-code-typos',
            icon: <AlertTriangle />,
            title: t('card.wrong_code_typos.title'),
            description: isEmailChannel(ctx)
              ? t('card.wrong_code_typos.body_email')
              : t('card.wrong_code_typos.body_sms'),
          }
        : null,
  },
  {
    id: 'iv/wrong-code-start-fresh',
    section: 'wrongCode',
    scenes: [VelaScene.IVContacts],
    priority: 10,
    evaluate: (ctx, t) =>
      ctx.contacts.hasError
        ? {
            id: 'wrong-code-start-fresh',
            icon: <RotateCw />,
            title: t('card.wrong_code_start_fresh.title'),
            description: isEmailChannel(ctx)
              ? t('card.wrong_code_start_fresh.body_email')
              : t('card.wrong_code_start_fresh.body_sms'),
          }
        : null,
  },
];
